import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
  getTrendingDestinationsCacheKey,
  getTrendingDestinationsForFilter,
  TrendingDestination,
} from 'home/components/TrendingDestinations/constants'
import { fetchTrendingDestinations } from 'actions/DestinationAlertsActions'
import { buildHotelUrl } from 'home/components/TrendingDestinations/utils'
import { EmptyArray } from 'lib/array/arrayUtils'

export function useHotelTrendingDestinations(
  selectedFilter: App.DestinationSelectedFilters,
  currentRegionCode: string,
  leUserId?: string,
  domainUserId?: string,
  disablePersonalisation?: boolean
): [Array<TrendingDestination>, boolean] {
  const dispatch = useAppDispatch()

  // Create the same cache key used by the action
  const userKey = getTrendingDestinationsCacheKey(leUserId, domainUserId, disablePersonalisation)

  useEffect(() => {
    dispatch(fetchTrendingDestinations(leUserId, domainUserId, disablePersonalisation))
  }, [dispatch, leUserId, domainUserId, disablePersonalisation])

  const trendingDestinations = useAppSelector(
    state => state.destination?.trendingDestinationsByUserKey?.[userKey],
  ) ?? EmptyArray

  const fetching = useAppSelector(
    state => state.destination?.fetchingTrendingDestinationsByUserKey?.[userKey] ?? false,
  )

  const displayTrending: Array<TrendingDestination> = useMemo(() => {
    if (fetching) return []

    return trendingDestinations
      .filter(destination => destination.imageId)
      .map(destination => ({
        name: destination.primaryText,
        imageId: destination.imageId,
        url: buildHotelUrl(destination.primaryText, destination.placeId),
        destinationId: destination.placeId,
      } as TrendingDestination))
  }, [trendingDestinations, fetching])

  if (selectedFilter !== 'hotel') {
    const hardCodedTrendingDestinations = getTrendingDestinationsForFilter(selectedFilter, currentRegionCode) ?? []
    return [hardCodedTrendingDestinations, false]
  }

  return [displayTrending, fetching]
}
